/// <amd-module name="Repos/Messages/ReposMessagesService" />
import { IReadonlyObservableArray, IReadonlyObservableValue, ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext, IVssService, ServiceOptions, Services, VssService } from "VSS/Platform/Context";
import { IRepositoryMessage } from "./Contracts";
import { IReposSuggestionsService } from "./ReposSuggestionsService";

export interface IReposMessagesService extends IVssService {
    /**
     * Gets the highest-priority Repos message (if any)
     *
     * @param loadSuggestions If true (the default), start loading messages from the suggestions service
     */
    getMessage(loadSuggestions?: boolean): IReadonlyObservableValue<IRepositoryMessage | undefined>;

    /**
     * Show the specified Repos message
     *
     * @param message The message to display
     * @param persistAcrossNextFps If true, persist the message across the next FPS navigation
     */
    showMessage(message: IRepositoryMessage, persistAcrossNextFps?: boolean): void;

    /**
     * Dismiss the current Repos message
     */
    dismissMessage(): void;
}

class ReposMessagesService extends VssService implements IReposMessagesService {
    private message = new ObservableValue<IRepositoryMessage | undefined>(undefined);
    private persistMessage? = false;
    private suggestionMessages?: IReadonlyObservableArray<IRepositoryMessage>;

    public _serviceRestart(pageContext: IVssPageContext) {
        super._serviceRestart(pageContext);

        if (this.suggestionMessages) {
            this.suggestionMessages.unsubscribe(this.onSuggestionsChanged);
            this.suggestionMessages = undefined;
        }

        if (!this.persistMessage) {
            this.message.value = undefined;
        }
        this.persistMessage = false;
    }

    public getMessage(loadSuggestions = true): IReadonlyObservableValue<IRepositoryMessage | undefined> {
        if (loadSuggestions && !this.suggestionMessages) {
            const messagesCollection = new ObservableCollection<IRepositoryMessage>();
            this.suggestionMessages = messagesCollection;

            // Add suggestions
            const suggestionsService = this.pageContext.getService<IReposSuggestionsService>("IReposSuggestionsService");
            messagesCollection.push(suggestionsService.getSuggestions(), suggestion => suggestionsService.getSuggestionMessage(suggestion));

            this.suggestionMessages.subscribe(this.onSuggestionsChanged);
            this.onSuggestionsChanged();
        }

        return this.message;
    }

    public showMessage(message: IRepositoryMessage, persistAcrossNextFps?: boolean): void {
        this.message.value = message;
        this.persistMessage = persistAcrossNextFps;
    }

    public dismissMessage(): void {
        if (this.message.value && this.message.value.onDismiss) {
            this.message.value.onDismiss();
        }
        this.message.value = undefined;
    }

    private onSuggestionsChanged = () => {
        if (!this.message.value && this.suggestionMessages && this.suggestionMessages.value.length) {
            this.showMessage(this.suggestionMessages.value[0], false);
        }
    };
}

const serviceName = "IReposMessagesService";
// Marking this service as persistant since it gives the ability to show messages across FPS boundaries
Services.add(serviceName, { serviceFactory: ReposMessagesService, options: ServiceOptions.Persistant });

export function getReposMessagesService(pageContext: IVssPageContext) {
    return pageContext.getService<IReposMessagesService>(serviceName);
}
